import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { Box, Fade, Grid, IconButton, Modal as MuiModal, ModalProps as MuiModalProps, Paper, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import Spinner from '../Spinner';

type ModalProps = {
  open: boolean;
  onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title: ReactNode;
  titleEndSlot?: ReactNode;
  loading?: boolean;
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
  scrollDisabled?: boolean;
  paper?: any;
  maxWidth?: number;
  noHeader?: boolean;
  onTransitionExited?: () => void;
};

const Modal = ({
  open,
  onClose,
  title,
  titleEndSlot,
  loading = false,
  children,
  icon,
  scrollDisabled,
  className = '',
  paper,
  maxWidth,
  noHeader = false,
  onTransitionExited
}: ModalProps) => {
  return (
    <StyledModal
      open={open}
      onClose={onClose as MuiModalProps['onClose']}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      className={className}
      onClick={(e) => e.stopPropagation()}
      onTransitionExited={onTransitionExited}
    >
      <Fade in={open} timeout={600}>
        <StyledPaper maxWidth={maxWidth} className={paper?.className}>
          {loading && (
            <LoadingSpinner>
              <Spinner size={80} style={{ marginTop: 0 }} />
            </LoadingSpinner>
          )}
          {!noHeader && (
            <ModalHeader container view={icon ? 'with-icon' : 'no-icon'}>
              <TitleSection className="modal-title-section">
                {!!icon && <HeaderIcon>{icon}</HeaderIcon>}
                <ModalTitle variant="h2">{title}</ModalTitle>
              </TitleSection>
              {titleEndSlot}
              <IconButton onClick={onClose as any} size="large" data-testid="modal-close-btn">
                <CloseButton icon="close" />
              </IconButton>
            </ModalHeader>
          )}

          <ModalBody className="modal-body" scroll={scrollDisabled ? 'disabled' : 'enabled'}>
            {children}
          </ModalBody>
        </StyledPaper>
      </Fade>
    </StyledModal>
  );
};

export default Modal;

const StyledModal = styled(MuiModal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => !['maxWidth'].includes(prop)
})<{ maxWidth?: number }>`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 0;
  min-height: 250px;
  padding: ${({ theme }) => theme.spacing(1, 2)};

  ${(props) => `max-width: ${props.maxWidth ?? 850}px;`}

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding: ${({ theme }) => theme.spacing(2, 4, 3)};
    width: 90vw;
    height: auto;
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  }

  &:focus-visible {
    outline: 0;
  }
`;

const LoadingSpinner = styled.div`
  background: rgba(0, 0, 0, 0.5);
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 0;
  top: 0;
  z-index: 9;
`;

const ModalHeader = styled(Grid)<{ view: 'no-icon' | 'with-icon' }>`
  padding: ${({ theme }) => theme.spacing(1, 2, 2)};
  border-bottom: ${({ theme }) => `1px solid ${theme.palette.divider}`};
  align-items: center;
  flex-wrap: nowrap;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding: ${({ theme }) => theme.spacing(0, 0.5, 1)};
  }
`;

const TitleSection = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  column-gap: ${({ theme }) => theme.spacing(1)};
`;

const HeaderIcon = styled.span`
  background: ${({ theme }) => theme.palette.primary.main + '3a'};
  margin-right: ${({ theme }) => theme.spacing(1)};
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.primary.main};
  width: 35px;
  height: 35px;
  border-radius: 30px;
  padding: ${({ theme }) => theme.spacing(0.75)};
  margin-left: ${({ theme }) => theme.spacing(-1)};
`;

const ModalTitle = styled(Typography)`
  font-size: 1.1rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightNormal};

  & > strong {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const CloseButton = styled(PolarisIcon)`
  font-size: 1.5rem;
  opacity: 0.5;
  transition: ease opacity 250ms;

  &:hover {
    opacity: 1;
    transition: ease opacity 250ms;
  }
`;

const ModalBody = styled(Box)<{ scroll: 'enabled' | 'disabled' }>`
  height: calc(100vh - 64px);
  overflow: ${({ scroll }) => (scroll === 'enabled' ? 'auto' : 'hidden')};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    height: auto;
    max-height: 88vh;
    padding-top: ${({ theme }) => theme.spacing(3)};
  }
`;
